import { useState, useEffect } from 'react'
import {
  HiOutlineFire,
  HiOutlineFilter,
  HiOutlineSortDescending,
  HiOutlineHome,
  HiOutlineTrendingUp,
  HiOutlineUsers,
  HiOutlineDocumentText,
  HiOutlineChartBar,
  HiOutlineMap,
  HiOutlineOfficeBuilding,
  HiOutlineGlobe,
  HiOutlineLocationMarker,
} from 'react-icons/hi'
import clsx from 'clsx'
import newsService from '../../services/newsService'
import MobileFilterModal from './MobileFilterModal'

// Category tabs cố định theo yêu cầu
const FIXED_CATEGORIES = [
  { id: 'all', label: 'Tất cả', icon: null },
  { id: 'hot', label: 'Tin nóng', icon: HiOutlineFire, isHot: true, color: '#ef4444' },
  { id: 'chinh_sach', label: 'Chính sách', icon: HiOutlineDocumentText, color: '#3b82f6' },
  { id: 'thi_truong', label: 'Thị trường', icon: HiOutlineChartBar, color: '#10b981' },
  { id: 'quy_hoach', label: 'Quy hoạch', icon: HiOutlineMap, color: '#8b5cf6' },
  { id: 'ha_tang', label: 'Hạ tầng', icon: HiOutlineOfficeBuilding, color: '#f59e0b' },
  { id: 'du_lich', label: 'Du lịch', icon: HiOutlineGlobe, color: '#06b6d4' },
]

// Địa phương ưu tiên miền Trung
const FIXED_LOCATIONS = [
  { id: '', label: 'Tất cả' },
  { id: 'quang-tri', label: 'Quảng Trị' },
  { id: 'hue', label: 'Huế' },
  { id: 'da-nang', label: 'Đà Nẵng' },
  { id: 'quang-ngai', label: 'Quảng Ngãi' },
  { id: 'gia-lai', label: 'Gia Lai' },
]

// Icon mapping cho customer types
const customerTypeIcons = {
  home: HiOutlineHome,
  'trending-up': HiOutlineTrendingUp,
  users: HiOutlineUsers,
}

function FilterBar({
  activeCategory = 'all',
  onCategoryChange,
  activeCustomerType = 'all',
  onCustomerTypeChange,
  sortBy = 'hot',
  onSortChange,
  location = '',
  onLocationChange,
  timeRange = '',
  onTimeRangeChange,
  sentiment = '',
  onSentimentChange,
  priceSegment = '',
  onPriceSegmentChange,
  showAdvanced = false,
  onFiltersChange,
}) {
  const [showFilters, setShowFilters] = useState(false)
  const [showMobileFilters, setShowMobileFilters] = useState(false)

  // Sử dụng categories và locations cố định
  const categories = FIXED_CATEGORIES
  const fixedLocations = FIXED_LOCATIONS

  // Dynamic data from API (chỉ còn customer types)
  const [customerTypes, setCustomerTypes] = useState([])
  const [locations, setLocations] = useState([]) // Cho advanced filter dropdown
  const [isLoadingCustomerTypes, setIsLoadingCustomerTypes] = useState(true)

  // Static options from service
  const sortOptions = newsService.getSortOptions()
  const timeRangeOptions = newsService.getTimeRangeOptions()
  const sentimentOptions = newsService.getSentimentOptions()
  const priceSegmentOptions = newsService.getPriceSegmentOptions()

  // Load dynamic data on mount (chỉ customer types và locations cho advanced filter)
  useEffect(() => {
    const loadFilterData = async () => {
      setIsLoadingCustomerTypes(true)
      try {
        const [custRes, locRes] = await Promise.all([
          newsService.getCustomerTypes(true),
          newsService.getLocations(true),
        ])

        // Transform customer types
        const custTypes = (custRes.data || []).map((ct) => ({
          id: ct.slug,
          label: ct.name,
          icon: customerTypeIcons[ct.icon] || HiOutlineUsers,
          color: ct.color,
          count: ct.news_count,
        }))
        setCustomerTypes(custTypes)

        // Transform locations cho advanced filter dropdown
        const locs = [
          { id: '', label: 'Tất cả khu vực' },
          ...(locRes.data || []).map((loc) => ({
            id: loc.slug,
            label: loc.name,
            count: loc.news_count,
          })),
        ]
        setLocations(locs)
      } catch {
        // Use fallback data
        setCustomerTypes([
          { id: 'all', label: 'Tất cả', icon: HiOutlineUsers },
          { id: 'mua_o', label: 'Mua ở', icon: HiOutlineHome },
          { id: 'dau_tu', label: 'Đầu tư', icon: HiOutlineTrendingUp },
        ])
        setLocations([
          { id: '', label: 'Tất cả khu vực' },
          { id: 'da-nang', label: 'Đà Nẵng' },
          { id: 'hue', label: 'Huế' },
          { id: 'quang-nam', label: 'Quảng Nam' },
        ])
      } finally {
        setIsLoadingCustomerTypes(false)
      }
    }

    loadFilterData()
  }, [])

  const handleCategoryClick = (cat) => {
    if (cat.isHot) {
      // Special handling for "Tin nóng" - triggers is_hot filter
      if (onFiltersChange) {
        onFiltersChange({ is_hot: true, category: '' })
      }
    } else if (cat.id === 'all') {
      if (onFiltersChange) {
        onFiltersChange({ is_hot: false, category: '' })
      }
    } else {
      if (onCategoryChange) {
        onCategoryChange(cat.id)
      }
      if (onFiltersChange) {
        onFiltersChange({ is_hot: false, category: cat.id })
      }
    }
  }

  const handleApplyFilters = () => {
    if (onFiltersChange) {
      onFiltersChange({
        location,
        time_range: timeRange,
        sentiment,
        price_segment: priceSegment,
      })
    }
    setShowFilters(false)
  }

  const handleResetFilters = () => {
    if (onLocationChange) onLocationChange('')
    if (onTimeRangeChange) onTimeRangeChange('')
    if (onSentimentChange) onSentimentChange('')
    if (onPriceSegmentChange) onPriceSegmentChange('')
  }

  // Mobile filter handlers
  const handleMobileFilterApply = (filters) => {
    if (onLocationChange) onLocationChange(filters.location)
    if (onTimeRangeChange) onTimeRangeChange(filters.timeRange)
    if (onSentimentChange) onSentimentChange(filters.sentiment)
    if (onPriceSegmentChange) onPriceSegmentChange(filters.priceSegment)
    if (onFiltersChange) {
      onFiltersChange({
        location: filters.location,
        time_range: filters.timeRange,
        sentiment: filters.sentiment,
        price_segment: filters.priceSegment,
      })
    }
  }

  // Count active advanced filters
  const activeFilterCount = [location, timeRange, sentiment, priceSegment].filter(Boolean).length

  return (
    <div className="card mb-6">
      {/* Customer type tabs (Mua ở / Đầu tư) */}
      {/* {customerTypes.length > 0 && (
        <div className="px-3 sm:px-4 py-2 border-b border-secondary-100 bg-secondary-50">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
            <span className="text-xs text-secondary-500 mr-1 sm:mr-2 whitespace-nowrap">Đối tượng:</span>
            {customerTypes.map((ct) => (
              <button
                key={ct.id}
                onClick={() => onCustomerTypeChange && onCustomerTypeChange(ct.id)}
                className={clsx(
                  'badge transition-all duration-200 flex items-center gap-1.5 whitespace-nowrap flex-shrink-0',
                  activeCustomerType === ct.id
                    ? 'text-white shadow-sm'
                    : 'bg-white text-secondary-600 hover:bg-secondary-100 border border-secondary-200'
                )}
                style={{
                  backgroundColor: activeCustomerType === ct.id ? (ct.color || '#3B82F6') : undefined,
                }}
              >
                {ct.icon && <ct.icon className="w-3.5 h-3.5" />}
                {ct.label}
                {ct.count > 0 && (
                  <span className={clsx(
                    'text-2xs',
                    activeCustomerType === ct.id ? 'opacity-75' : 'text-secondary-400'
                  )}>
                    ({ct.count})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )} */}

      {/* Category tabs - Responsive: scroll horizontal on mobile */}
      <div className="px-3 py-3 flex flex-col gap-3">
        {/* Categories - scrollable */}
        <div className="overflow-x-auto scrollbar-hide -mx-3 px-3">
          <div className="flex gap-2 min-w-max">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryClick(cat)}
                className={clsx(
                  'badge transition-all duration-200 flex items-center gap-1.5 press-scale whitespace-nowrap',
                  activeCategory === cat.id
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                )}
                style={{
                  backgroundColor: activeCategory === cat.id && cat.color ? cat.color : undefined,
                }}
              >
                {cat.icon && <cat.icon className="w-3.5 h-3.5" />}
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Location filter row - địa phương miền Trung */}
        <div className="overflow-x-auto scrollbar-hide -mx-3 px-3 border-t border-secondary-100 pt-3">
          <div className="flex items-center gap-2">
            <HiOutlineLocationMarker className="w-4 h-4 text-secondary-400 flex-shrink-0" />
            <div className="flex gap-1.5 min-w-max">
              {fixedLocations.map((loc) => (
                <button
                  key={loc.id}
                  onClick={() => onLocationChange && onLocationChange(loc.id)}
                  className={clsx(
                    'px-2.5 py-1 text-xs rounded-lg transition-all duration-200 whitespace-nowrap',
                    location === loc.id
                      ? 'bg-emerald-100 text-emerald-700 font-medium'
                      : 'bg-secondary-50 text-secondary-600 hover:bg-secondary-100'
                  )}
                >
                  {loc.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sort dropdown and filter button */}
        <div className="flex items-center justify-between border-t border-secondary-100 pt-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-secondary-500">Sắp xếp:</span>
            <HiOutlineSortDescending className="w-4 h-4 text-secondary-400" />
            <select
              value={sortBy}
              onChange={(e) => onSortChange && onSortChange(e.target.value)}
              className="text-sm border-0 bg-transparent text-secondary-700 font-medium focus:ring-0 cursor-pointer pr-6"
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Filter button */}
          {showAdvanced && (
            <div className="flex items-center gap-2">
              {/* Desktop */}
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={clsx(
                  'hidden md:flex btn btn-sm items-center gap-1.5',
                  showFilters || activeFilterCount > 0
                    ? 'bg-primary-50 border-primary-200 text-primary-700'
                    : 'btn-secondary'
                )}
              >
                <HiOutlineFilter className="w-4 h-4" />
                <span>Lọc</span>
                {activeFilterCount > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-primary-600 text-white rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Mobile */}
              <button
                type="button"
                onClick={() => setShowMobileFilters(true)}
                className={clsx(
                  'md:hidden btn btn-sm flex items-center gap-1.5 relative',
                  activeFilterCount > 0
                    ? 'bg-primary-50 border-primary-200 text-primary-700'
                    : 'btn-secondary'
                )}
              >
                <HiOutlineFilter className="w-4 h-4" />
                <span>Lọc</span>
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 text-2xs bg-primary-600 text-white rounded-full flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Advanced filters - Desktop only */}
      {showAdvanced && showFilters && (
        <div className="hidden md:block px-4 py-4 border-t border-secondary-100 bg-secondary-50 rounded-b-xl animate-slide-down">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Location filter */}
            <div className="form-group">
              <label className="label">Khu vực</label>
              <select
                value={location}
                onChange={(e) => onLocationChange && onLocationChange(e.target.value)}
                className="select"
              >
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.label}
                    {loc.count > 0 && ` (${loc.count})`}
                  </option>
                ))}
              </select>
            </div>

            {/* Time range filter */}
            <div className="form-group">
              <label className="label">Thời gian</label>
              <select
                value={timeRange}
                onChange={(e) => onTimeRangeChange && onTimeRangeChange(e.target.value)}
                className="select"
              >
                {timeRangeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sentiment filter */}
            <div className="form-group">
              <label className="label">Xu hướng tin</label>
              <select
                value={sentiment}
                onChange={(e) => onSentimentChange && onSentimentChange(e.target.value)}
                className="select"
              >
                {sentimentOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Price segment filter */}
            <div className="form-group">
              <label className="label">Phân khúc giá</label>
              <select
                value={priceSegment}
                onChange={(e) => onPriceSegmentChange && onPriceSegmentChange(e.target.value)}
                className="select"
              >
                {priceSegmentOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end gap-3 mt-4">
            <button type="button" onClick={handleResetFilters} className="btn-ghost btn-sm">
              Xóa bộ lọc
            </button>
            <button type="button" onClick={() => setShowFilters(false)} className="btn-ghost btn-sm">
              Đóng
            </button>
            <button type="button" onClick={handleApplyFilters} className="btn-primary btn-sm">
              Áp dụng
            </button>
          </div>
        </div>
      )}

      {/* Mobile Filter Modal */}
      {showAdvanced && (
        <MobileFilterModal
          isOpen={showMobileFilters}
          onClose={() => setShowMobileFilters(false)}
          location={location}
          timeRange={timeRange}
          sentiment={sentiment}
          priceSegment={priceSegment}
          locations={locations}
          timeRangeOptions={timeRangeOptions}
          sentimentOptions={sentimentOptions}
          priceSegmentOptions={priceSegmentOptions}
          onApply={handleMobileFilterApply}
          onReset={handleResetFilters}
        />
      )}
    </div>
  )
}

export default FilterBar
